import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles } from "../shared/styles";
import { BackButton } from "../shared/BackButton";
import {
  LuPlus,
  LuFileDown,
  LuClipboardCheck,
  LuCircleAlert,
  LuUser,
  LuPencil,
  LuTrash2,
} from "react-icons/lu";
import { generateRectificationQuotePDF } from "../generateRectificationQuotePDF";
import { generateRectificationSummaryPDF } from "../generateRectificationSummaryPDF";
import { generateRectificationOverviewPDF } from "../generateRectificationOverviewPDF";
import { generateRectificationRequestPDF } from "../generateRectificationRequestPDF";
import { useIsMobile } from "../shared/utils";

const _toDataURL = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.width; c.height = img.height;
      c.getContext("2d").drawImage(img, 0, 0);
      resolve(c.toDataURL("image/png").split(",")[1]);
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

async function saveQuoteBlobToFolder(quoteData, blob, efsm) {
  const year = quoteData.inspection_date
    ? new Date(quoteData.inspection_date).getFullYear()
    : new Date().getFullYear();
  const typeLabel = { annual: "Annual", bi_annual: "Bi-Annual", monthly: "Monthly" }[quoteData.inspection_type] || "Inspection";
  const folderName = `${typeLabel} ${year}`;
  const foldersRes = await axios.get(`${API}/site-folders/?site=${quoteData.inspection_site_id}`);
  let folder = foldersRes.data.find((f) => f.name === folderName);
  if (!folder) {
    const cr = await axios.post(`${API}/site-folders/`, {
      site: quoteData.inspection_site_id,
      name: folderName,
      is_system: true,
    });
    folder = cr.data;
  }
  const siteName = (quoteData.inspection_site_name || "site").replace(/[^a-z0-9]/gi, "_");
  const efsmSlug = efsm ? `_${efsm.replace(/[^a-z0-9]/gi, "_")}` : "";
  const fileName = `RQ-${String(quoteData.id).padStart(3, "0")}_${siteName}${efsmSlug}_Quote.pdf`;
  // Delete any existing doc with the same EFSM slug regardless of quote ID (handles duplicate quotes)
  const slugSuffix = `${efsmSlug}_Quote.pdf`;
  const stale = (folder.documents || []).filter((d) =>
    efsmSlug ? d.name.endsWith(slugSuffix) : d.name === fileName
  );
  await Promise.all(stale.map((d) => axios.delete(`${API}/site-documents/${d.id}/?skip_sync=1`).catch(() => {})));
  const pdfFile = new File([blob], fileName, { type: "application/pdf" });
  const fd = new FormData();
  fd.append("file", pdfFile);
  fd.append("folder", folder.id);
  await axios.post(`${API}/site-documents/`, fd);
}

async function silentFileSummaryQuotePDF(quoteData) {
  try {
    const siblingsRes = await axios.get(`${API}/rectification-quotes/?inspection=${quoteData.inspection}`);
    const siblings = siblingsRes.data || [];
    if (siblings.length === 0) return;
    const blob = generateRectificationOverviewPDF(siblings, { noDownload: true });
    if (!blob) return;
    // Use lowest-ID sibling as anchor so filename is stable across re-generations
    const anchor = siblings.reduce((min, q) => (q.id < min.id ? q : min), siblings[0]);
    await saveQuoteBlobToFolder(anchor, blob, "SummaryQuotation");
  } catch (e) {
    console.warn("Auto-file summary quotation failed:", e);
  }
}

async function silentFileQuotePDF(quoteData) {
  try {
    const sigsRes = await axios.get(`${API}/tech-signatures/`);
    const signaturesMap = {};
    for (const s of sigsRes.data) {
      if (s.signature_url) signaturesMap[s.tech_name] = await _toDataURL(s.signature_url);
    }
    let efsm = null;
    if (quoteData.site_large_building) {
      const names = [...new Set((quoteData.items || [])
        .map((i) => i.inspection_item_detail?.category_name)
        .filter(Boolean))];
      if (names.length === 1) efsm = names[0];
    }
    const blob = await generateRectificationQuotePDF(quoteData, signaturesMap, efsm ? { efsm, noDownload: true } : { noDownload: true });
    await saveQuoteBlobToFolder(quoteData, blob, efsm);
  } catch (e) {
    console.warn("Auto-file quote failed:", e);
  }
}

const C = {
  navy: "#1e3a5f",
  teal: "#0891b2",
  border: "#e2e8f0",
  bg: "#f8fafc",
  white: "#ffffff",
  slate: "#64748b",
  amber: "#f59e0b",
  green: "#16a34a",
  red: "#e63946",
  orange: "#ea580c",
};

const STATUS_COLORS = {
  draft: { bg: "#f1f5f9", color: "#64748b" },
  sent: { bg: "#eff6ff", color: "#1d4ed8" },
  approved: { bg: "#f0fdf4", color: "#16a34a" },
  rejected: { bg: "#fff1f2", color: "#e11d48" },
  pending: { bg: "#fff7ed", color: "#ea580c" },
  in_progress: { bg: "#eff6ff", color: "#1d4ed8" },
  completed: { bg: "#f0fdf4", color: "#16a34a" },
  unassigned: { bg: "#f1f5f9", color: "#64748b" },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span
      style={{
        backgroundColor: s.bg,
        color: s.color,
        border: `1px solid ${s.color}40`,
        borderRadius: "999px",
        padding: "2px 10px",
        fontSize: "11px",
        fontWeight: "600",
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

function btn(color, outline = false) {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: outline ? C.white : color,
    color: outline ? color : C.white,
    border: `1.5px solid ${color}`,
    borderRadius: "6px",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };
}

// ── Quote Form ────────────────────────────────────────────────────────────────

function QuoteForm({ inspections, quotes, onSaved, onCancel }) {
  const isMobile = useIsMobile();
  const [selectedInspection, setSelectedInspection] = useState("");
  const [failItems, setFailItems] = useState([]);
  const [isLargeBuilding, setIsLargeBuilding] = useState(false);
  const [selectedEFSM, setSelectedEFSM] = useState("");
  const [prices, setPrices] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [servicePriceMap, setServicePriceMap] = useState({});
  const [servicePrices, setServicePrices] = useState([]);
  const [selectedPriceItemIds, setSelectedPriceItemIds] = useState({});

  useEffect(() => {
    axios.get(`${API}/service-prices/?active=true`).then((r) => {
      const list = r.data || [];
      const map = {};
      for (const sp of list) {
        if (sp.asset_type) {
          if (!map[sp.asset_type]) map[sp.asset_type] = [];
          map[sp.asset_type].push(sp);
        }
      }
      setServicePrices(list);
      setServicePriceMap(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedInspection) { setFailItems([]); setIsLargeBuilding(false); setSelectedEFSM(""); return; }
    axios.get(`${API}/inspections/${selectedInspection}/`).then((r) => {
      const fails = (r.data.items || []).filter((it) => it.result === "fail" && !it.swapped);
      setFailItems(fails);
      setIsLargeBuilding(!!r.data.site_large_building);
      setSelectedEFSM("");
      const p = {}, sel = {};
      fails.forEach((it) => {
        const matches = it.asset_type_id != null ? (servicePriceMap[it.asset_type_id] || []) : [];
        const auto = matches.length === 1 ? matches[0] : null;
        p[it.id] = auto ? String(auto.price_ex_gst) : "";
        sel[it.id] = auto ? String(auto.id) : "";
      });
      setPrices(p);
      setSelectedPriceItemIds(sel);
    });
  }, [selectedInspection]);

  const existingQuotes = (quotes || []).filter((q) => q.inspection === Number(selectedInspection));
  const quotedItemIds = new Set(existingQuotes.flatMap((q) => (q.items || []).map((it) => it.inspection_item)));
  const displayItems = (isLargeBuilding && selectedEFSM)
    ? failItems.filter((it) => it.category_name === selectedEFSM && !quotedItemIds.has(it.id))
    : failItems.filter((it) => !quotedItemIds.has(it.id));
  const totalEx = displayItems.reduce((a, it) => a + (parseFloat(prices[it.id]) || 0), 0);
  const gst = totalEx * 0.10;
  const totalInc = totalEx * 1.10;
  const fmt2 = (n) => n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const inspectionsNeedingQuote = (inspections || []).filter((i) => {
    if (i.status !== "failed") return false;
    const failItems = (i.items || []).filter((it) => it.result === "fail" && !it.swapped);
    if (failItems.length === 0) return false;
    const quotedIds = new Set(
      (quotes || []).filter((q) => q.inspection === i.id).flatMap((q) => (q.items || []).map((it) => it.inspection_item))
    );
    return failItems.some((it) => !quotedIds.has(it.id));
  });

  const handleSave = async () => {
    if (!selectedInspection) { setError("Please select an inspection."); return; }
    if (displayItems.length === 0) { setError("No fail items to quote for this selection."); return; }
    setSaving(true); setError("");
    try {
      const quoteRes = await axios.post(`${API}/rectification-quotes/`, {
        inspection: Number(selectedInspection),
        notes,
      });
      const quoteId = quoteRes.data.id;
      for (const item of displayItems) {
        const selectedName = servicePrices.find((s) => String(s.id) === selectedPriceItemIds[item.id])?.name || "";
        await axios.post(`${API}/rectification-quote-items/`, {
          quote: quoteId,
          inspection_item: item.id,
          proposed_solution: selectedName,
          price_ex_gst: parseFloat(prices[item.id]) || 0,
        });
      }
      const freshRes = await axios.get(`${API}/rectification-quotes/${quoteId}/`);
      silentFileQuotePDF(freshRes.data);
      if (freshRes.data.site_large_building) silentFileSummaryQuotePDF(freshRes.data);
      onSaved(quoteId);
    } catch (e) {
      const errData = e.response?.data;
      if (!errData) { setError("Save failed."); }
      else if (typeof errData === "string") { setError(errData); }
      else if (errData.detail) { setError(errData.detail); }
      else {
        setError(Object.entries(errData)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
          .join(" | "));
      }
    } finally {
      setSaving(false);
    }
  };

  const showLeftPanel = !selectedInspection || (isLargeBuilding && !selectedEFSM) || isMobile;
  const selectedInspectionObj = inspections.find((i) => String(i.id) === selectedInspection);
  const handleChangeInspection = () => { setSelectedInspection(""); setFailItems([]); setSelectedEFSM(""); setIsLargeBuilding(false); };
  const linkBtn = { fontSize: "12px", color: C.navy, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0, marginLeft: "8px" };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
        <BackButton onClick={onCancel} />
        <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: C.navy }}>New Rectification Quote</h2>
      </div>

      {/* Two-column body */}
      <div style={{ display: "flex", gap: isMobile ? "24px" : "0", alignItems: "flex-start", flexDirection: isMobile ? "column" : "row" }}>

        {/* LEFT: selection panel — hidden on desktop once selection complete */}
        {showLeftPanel && <div style={{
          width: isMobile ? "100%" : "380px",
          flexShrink: 0,
          backgroundColor: C.bg,
          border: isMobile ? `1px solid ${C.border}` : "none",
          borderRight: isMobile ? "none" : `1px solid ${C.border}`,
          borderRadius: isMobile ? "10px" : "0",
          padding: isMobile ? "16px" : "20px 24px",
          minHeight: isMobile ? "auto" : "calc(100vh - 140px)",
        }}>
          {!selectedInspection ? (
            <>
              <div style={{ fontSize: "12px", fontWeight: "700", color: C.slate, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>
                Select Inspection
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: isMobile ? "320px" : "calc(100vh - 240px)", overflowY: "auto" }}>
                {inspectionsNeedingQuote.map((i) => (
                  <div
                    key={i.id}
                    onClick={() => setSelectedInspection(String(i.id))}
                    style={{ border: `1px solid ${C.border}`, borderRadius: "8px", padding: "10px 14px", cursor: "pointer", backgroundColor: C.bg }}
                  >
                    <div style={{ fontWeight: "600", color: C.navy, fontSize: "13px" }}>{i.site_name}</div>
                    <div style={{ fontSize: "12px", color: C.slate, marginTop: "2px" }}>
                      {i.inspection_type?.replace("_", "-")} · {i.date ? new Date(i.date).toLocaleDateString("en-AU") : ""}
                    </div>
                  </div>
                ))}
                {inspectionsNeedingQuote.length === 0 && (
                  <div style={{ color: C.slate, fontSize: "13px", padding: "8px 0" }}>No failed inspections with unquoted defects.</div>
                )}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: "12px", fontWeight: "700", color: C.slate, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>
                Inspection
              </div>
              <div style={{ padding: "10px 14px", border: `1px solid ${C.border}`, borderRadius: "8px", backgroundColor: "#eef2ff", marginBottom: isLargeBuilding ? "20px" : 0 }}>
                <div style={{ fontWeight: "600", color: C.navy, fontSize: "13px" }}>
                  {inspections.find((i) => String(i.id) === selectedInspection)?.site_name}
                </div>
                <div style={{ fontSize: "12px", color: C.slate, marginTop: "2px" }}>
                  {(() => { const i = inspections.find((x) => String(x.id) === selectedInspection); return i ? `${(i.inspection_type || "").replace("_", "-")} · ${i.date ? new Date(i.date).toLocaleDateString("en-AU") : ""}` : ""; })()}
                </div>
                <button
                  onClick={() => { setSelectedInspection(""); setFailItems([]); setSelectedEFSM(""); setIsLargeBuilding(false); }}
                  style={{ fontSize: "12px", color: C.navy, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0, marginTop: "6px" }}
                >
                  Change
                </button>
              </div>

              {/* EFSM selector — large building, no EFSM chosen yet */}
              {isLargeBuilding && !selectedEFSM && failItems.length > 0 && (
                <>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: C.slate, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>
                    Select EFSM
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {[...new Set(failItems.filter((it) => !quotedItemIds.has(it.id)).map((it) => it.category_name).filter(Boolean))].sort().map((efsm) => {
                      const count = failItems.filter((it) => it.category_name === efsm && !quotedItemIds.has(it.id)).length;
                      return (
                        <div
                          key={efsm}
                          onClick={() => setSelectedEFSM(efsm)}
                          style={{ border: `1px solid ${C.border}`, borderRadius: "8px", padding: "10px 14px", cursor: "pointer", backgroundColor: C.bg, display: "flex", alignItems: "center", justifyContent: "space-between" }}
                        >
                          <div style={{ fontWeight: "600", color: C.navy, fontSize: "13px" }}>{efsm}</div>
                          <span style={{ fontSize: "12px", color: C.red, fontWeight: "600" }}>{count} fail{count !== 1 ? "s" : ""}</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* EFSM badge — shown once EFSM selected */}
              {isLargeBuilding && selectedEFSM && (
                <>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: C.slate, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>
                    EFSM
                  </div>
                  <div style={{ padding: "10px 14px", border: `1px solid ${C.navy}40`, borderRadius: "8px", backgroundColor: "#eef2ff" }}>
                    <div style={{ fontWeight: "600", color: C.navy, fontSize: "13px" }}>{selectedEFSM}</div>
                    <button
                      onClick={() => setSelectedEFSM("")}
                      style={{ fontSize: "12px", color: C.navy, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0, marginTop: "6px" }}
                    >
                      Change EFSM
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>}

        {/* RIGHT: work area */}
        <div style={{ flex: 1, minWidth: 0, paddingLeft: isMobile || showLeftPanel ? "0" : "32px" }}>
          {!selectedInspection || (isLargeBuilding && !selectedEFSM) ? (
            <div style={{
              backgroundColor: C.bg,
              border: isMobile ? `1px solid ${C.border}` : "none",
              borderRadius: isMobile ? "10px" : "0",
              padding: isMobile ? "60px 24px" : "60px 40px",
              textAlign: "center",
              color: C.slate,
              fontSize: "14px",
            }}>
              {!selectedInspection ? "Select an inspection to begin quoting." : "Select an EFSM to begin quoting."}
            </div>
          ) : (
            <>
              {/* Compact info bar — desktop only, replaces left panel once selection is made */}
              {!isMobile && selectedInspectionObj && (
                <div style={{ display: "flex", alignItems: "center", gap: "0", backgroundColor: "#eef2ff", border: `1px solid ${C.border}`, borderRadius: "8px", padding: "10px 16px", marginBottom: "16px", fontSize: "13px", flexWrap: "wrap", rowGap: "4px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontWeight: "600", color: C.navy }}>{selectedInspectionObj.site_name}</span>
                    <span style={{ color: C.slate }}>· {(selectedInspectionObj.inspection_type || "").replace("_", "-")} · {selectedInspectionObj.date ? new Date(selectedInspectionObj.date).toLocaleDateString("en-AU") : ""}</span>
                    <button onClick={handleChangeInspection} style={linkBtn}>Change</button>
                  </div>
                  {isLargeBuilding && selectedEFSM && (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", borderLeft: `1px solid ${C.border}`, marginLeft: "16px", paddingLeft: "16px" }}>
                      <span style={{ color: C.slate }}>EFSM:</span>
                      <span style={{ fontWeight: "600", color: C.navy }}>{selectedEFSM}</span>
                      <button onClick={() => setSelectedEFSM("")} style={linkBtn}>Change</button>
                    </div>
                  )}
                </div>
              )}
              {displayItems.length > 0 ? (
                <>
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: "10px", overflow: "hidden", marginBottom: "20px" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                      <thead>
                        <tr style={{ backgroundColor: C.navy, color: C.white }}>
                          <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600" }}>#</th>
                          <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600" }}>Asset / Location</th>
                          <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600" }}>Defect / Photos</th>
                          <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600", minWidth: "180px" }}>Service Item</th>
                          <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: "600", minWidth: "120px" }}>Price (ex. GST)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayItems.map((item, idx) => {
                          const photos = [item.photo_1_url, item.photo_2_url, item.photo_3_url].filter(Boolean);
                          const options = item.asset_type_id != null ? (servicePriceMap[item.asset_type_id] || []) : [];
                          return (
                            <tr key={item.id} style={{ backgroundColor: idx % 2 === 0 ? C.white : C.bg }}>
                              <td style={{ padding: "10px 12px", color: C.slate, verticalAlign: "top" }}>{idx + 1}</td>
                              <td style={{ padding: "10px 12px", verticalAlign: "top" }}>
                                <div style={{ fontWeight: "600", color: C.navy }}>{item.asset_type_name}</div>
                                <div style={{ color: C.slate, fontSize: "12px" }}>{item.floor_level} · {item.location}</div>
                              </td>
                              <td style={{ padding: "10px 12px", verticalAlign: "top", maxWidth: "200px" }}>
                                <div style={{ color: C.slate, fontSize: "12px", marginBottom: photos.length ? "8px" : 0 }}>{item.notes}</div>
                                {photos.length > 0 && (
                                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                    {photos.map((url, pi) => (
                                      <img key={pi} src={url} alt={`Photo ${pi + 1}`} style={{ width: "64px", height: "48px", objectFit: "cover", borderRadius: "4px", border: `1px solid ${C.border}` }} />
                                    ))}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: "10px 12px", verticalAlign: "top" }}>
                                {options.length > 0 ? (
                                  <select
                                    value={selectedPriceItemIds[item.id] || ""}
                                    onChange={(e) => {
                                      const spId = e.target.value;
                                      setSelectedPriceItemIds((prev) => ({ ...prev, [item.id]: spId }));
                                      if (spId) {
                                        const sp = options.find((s) => String(s.id) === spId);
                                        if (sp) setPrices((prev) => ({ ...prev, [item.id]: String(sp.price_ex_gst) }));
                                      }
                                    }}
                                    style={{ width: "100%", fontSize: "12px", padding: "5px 6px", border: `1px solid ${C.border}`, borderRadius: "4px", color: C.navy, backgroundColor: C.white }}
                                  >
                                    <option value="">— Select —</option>
                                    {options.map((sp) => (
                                      <option key={sp.id} value={String(sp.id)}>{sp.name}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <span style={{ fontSize: "11px", color: C.slate, fontStyle: "italic" }}>No items linked</span>
                                )}
                              </td>
                              <td style={{ padding: "10px 12px", verticalAlign: "top" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                  <span style={{ color: C.slate }}>$</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={prices[item.id] || ""}
                                    onChange={(e) => setPrices((p) => ({ ...p, [item.id]: e.target.value }))}
                                    placeholder="0.00"
                                    style={{ flex: 1, padding: "5px 8px", border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "13px", textAlign: "right" }}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Totals */}
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
                    <div style={{ border: `1px solid ${C.border}`, borderRadius: "8px", overflow: "hidden", minWidth: "280px" }}>
                      {[
                        { label: "Subtotal (ex. GST)", value: `$${fmt2(totalEx)}` },
                        { label: "GST (10%)", value: `$${fmt2(gst)}` },
                      ].map(({ label, value }) => (
                        <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "9px 16px", borderBottom: `1px solid ${C.border}`, fontSize: "13px" }}>
                          <span style={{ color: C.slate }}>{label}</span>
                          <span style={{ color: C.navy, fontWeight: "600" }}>{value}</span>
                        </div>
                      ))}
                      <div style={{ display: "flex", justifyContent: "space-between", padding: "11px 16px", backgroundColor: C.navy, fontSize: "14px" }}>
                        <span style={{ color: C.white, fontWeight: "700" }}>TOTAL (inc. GST)</span>
                        <span style={{ color: C.white, fontWeight: "700" }}>${fmt2(totalInc)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div style={{ marginBottom: "20px" }}>
                    <label style={{ fontSize: "13px", fontWeight: "600", color: C.navy, display: "block", marginBottom: "6px" }}>Notes (optional)</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      style={{ width: "100%", padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: "6px", fontSize: "13px" }}
                    />
                  </div>
                </>
              ) : (
                <div style={{ backgroundColor: C.bg, border: `1px solid ${C.border}`, borderRadius: "10px", padding: "60px 24px", textAlign: "center", color: C.slate, fontSize: "14px", marginBottom: "20px" }}>
                  All defects for this selection are already quoted.
                </div>
              )}

              {error && <div style={{ color: C.red, fontSize: "13px", marginBottom: "12px" }}>{error}</div>}

              <div style={{ display: "flex", gap: "10px" }}>
                <button style={btn(C.slate, true)} onClick={onCancel}>Cancel</button>
                {displayItems.length > 0 && (
                  <button style={btn(C.navy)} onClick={handleSave} disabled={saving}>
                    {saving ? "Saving…" : "Save Rectification Quote"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Quote Detail ──────────────────────────────────────────────────────────────

function QuoteDetail({ quoteId, onBack, onRequestCreated }) {
  const [quote, setQuote] = useState(null);
  const [techs, setTechs] = useState([]);
  const [assignedTo, setAssignedTo] = useState("");
  const [creatingRequest, setCreatingRequest] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editNotes, setEditNotes] = useState("");
  const [editItems, setEditItems] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [siblingQuotes, setSiblingQuotes] = useState([]);

  const load = useCallback(() => {
    axios.get(`${API}/rectification-quotes/${quoteId}/`).then((r) => {
      setQuote(r.data);
      if (r.data.site_large_building) {
        axios.get(`${API}/rectification-quotes/?inspection=${r.data.inspection}`)
          .then((sr) => setSiblingQuotes(sr.data || []));
      }
    });
  }, [quoteId]);

  useEffect(() => {
    load();
    axios.get(`${API}/technicians/`).then((r) => setTechs(r.data));
  }, [load]);

  if (!quote) return <div style={{ padding: "40px", color: C.slate }}>Loading…</div>;

  const fmt2 = (n) => Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const loadSigsMap = async () => {
    const map = {};
    try {
      const sigsRes = await axios.get(`${API}/tech-signatures/`);
      const toDataURL = (url) => new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => { const c = document.createElement("canvas"); c.width = img.width; c.height = img.height; c.getContext("2d").drawImage(img, 0, 0); resolve(c.toDataURL("image/png").split(",")[1]); };
        img.onerror = () => resolve(null);
        img.src = url;
      });
      for (const s of sigsRes.data) {
        if (s.signature_url) map[s.tech_name] = await toDataURL(s.signature_url);
      }
    } catch (e) { console.warn("Could not load tech signatures", e); }
    return map;
  };

  const handlePDF = async (efsm) => {
    setGeneratingPDF(true);
    try {
      const signaturesMap = await loadSigsMap();
      const blob = await generateRectificationQuotePDF(quote, signaturesMap, efsm ? { efsm } : {});
      saveQuoteBlobToFolder(quote, blob, efsm || null).catch((e) => console.warn("folder save failed:", e));
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleSummaryPDF = async () => {
    setGeneratingPDF(true);
    try {
      const signaturesMap = await loadSigsMap();
      const blob = await generateRectificationSummaryPDF(siblingQuotes, signaturesMap, {});
      saveQuoteBlobToFolder(quote, blob, "Summary").catch((e) => console.warn("folder save failed:", e));
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleOverviewPDF = () => {
    setGeneratingPDF(true);
    try {
      const blob = generateRectificationOverviewPDF(siblingQuotes);
      saveQuoteBlobToFolder(quote, blob, "Overview").catch((e) => console.warn("folder save failed:", e));
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatusUpdating(true);
    try {
      await axios.patch(`${API}/rectification-quotes/${quote.id}/`, { status: newStatus });
      load();
      if (newStatus === "approved") {
        silentFileQuotePDF(quote);
      }
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!assignedTo) return;
    setCreatingRequest(true);
    try {
      const r = await axios.post(`${API}/rectification-requests/`, {
        quote: quote.id,
        assigned_to: Number(assignedTo),
      });
      onRequestCreated(r.data.id);
    } finally {
      setCreatingRequest(false);
    }
  };

  const handleDelete = async () => {
    const msg = quote.has_request
      ? "Delete this quote? The linked rectification request will also be deleted. This cannot be undone."
      : "Delete this quote? This cannot be undone.";
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/rectification-quotes/${quote.id}/`);
      // Clean up any filed PDFs for this quote from site records
      const prefix = `RQ-${String(quote.id).padStart(3, "0")}_`;
      axios.get(`${API}/site-folders/?site=${quote.inspection_site_id}`)
        .then((res) => {
          const docsToDelete = (res.data || []).flatMap((folder) =>
            (folder.documents || []).filter((d) => d.name.startsWith(prefix) && d.name.endsWith("_Quote.pdf"))
          );
          docsToDelete.forEach((d) => axios.delete(`${API}/site-documents/${d.id}/`).catch(console.warn));
        })
        .catch(console.warn);
      onBack();
    } catch (e) {
      alert("Delete failed: " + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
      setDeleting(false);
    }
  };

  const handleEdit = () => {
    setEditNotes(quote.notes || "");
    const map = {};
    (quote.items || []).forEach((it) => {
      map[it.id] = { proposed_solution: it.proposed_solution || "", price_ex_gst: it.price_ex_gst || "" };
    });
    setEditItems(map);
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/rectification-quotes/${quote.id}/`, { notes: editNotes });
      await Promise.all(
        Object.entries(editItems).map(([id, vals]) =>
          axios.patch(`${API}/rectification-quote-items/${id}/`, {
            proposed_solution: vals.proposed_solution,
            price_ex_gst: parseFloat(vals.price_ex_gst) || 0,
          })
        )
      );
      setEditMode(false);
      load();
      const freshRes = await axios.get(`${API}/rectification-quotes/${quote.id}/`);
      silentFileQuotePDF(freshRes.data);
    } catch (e) {
      alert("Save failed: " + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
        <BackButton onClick={onBack} />
        <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: C.navy, flex: 1 }}>
          RQ-{String(quote.id).padStart(3, "0")} — {quote.inspection_site_name}
        </h2>
      </div>

      {/* Status actions */}
      {quote.status === "draft" && (
        <div style={{ marginBottom: "16px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button style={btn(C.teal, true)} onClick={() => handleStatusChange("sent")} disabled={statusUpdating}>Mark as Sent</button>
        </div>
      )}
      {quote.status === "sent" && (
        <div style={{ marginBottom: "16px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button style={btn(C.green)} onClick={() => handleStatusChange("approved")} disabled={statusUpdating}>Mark Approved</button>
          <button style={btn(C.red, true)} onClick={() => handleStatusChange("rejected")} disabled={statusUpdating}>Mark Rejected</button>
        </div>
      )}

      {/* Create Request (once approved, no existing request) */}
      {quote.status === "approved" && !quote.has_request && (
        <div style={{ backgroundColor: "#f0fdf4", border: `1px solid ${C.green}40`, borderRadius: "8px", padding: "16px", marginBottom: "20px", display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <LuClipboardCheck size={20} color={C.green} />
          <span style={{ fontSize: "14px", color: C.navy, fontWeight: "600", flex: 1 }}>Quote approved — create a Rectification Request to assign works to a technician.</span>
          <select
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            style={{ padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: "6px", fontSize: "13px" }}
          >
            <option value="">— Assign tech —</option>
            {techs.map((t) => (
              <option key={t.id} value={t.id}>
                {[t.first_name, t.last_name].filter(Boolean).join(" ") || t.username}
              </option>
            ))}
          </select>
          <button style={btn(C.green)} onClick={handleCreateRequest} disabled={!assignedTo || creatingRequest}>
            {creatingRequest ? "Creating…" : "Create Request"}
          </button>
        </div>
      )}

      {/* Notes field (edit mode only) */}
      {editMode && (
        <div style={{ marginBottom: "16px" }}>
          <label style={{ fontSize: "13px", fontWeight: "600", color: C.navy, display: "block", marginBottom: "6px" }}>Notes</label>
          <textarea
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            rows={2}
            style={{ width: "100%", maxWidth: "600px", padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: "6px", fontSize: "13px" }}
          />
        </div>
      )}

      {/* Items table */}
      <div style={{ overflowX: "auto", marginBottom: "20px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ backgroundColor: C.navy, color: C.white }}>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>#</th>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>Asset / Location</th>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>Defect</th>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>Proposed Solution</th>
              <th style={{ padding: "8px 10px", textAlign: "right" }}>Price (ex. GST)</th>
            </tr>
          </thead>
          <tbody>
            {(quote.items || []).map((item, idx) => {
              const d = item.inspection_item_detail || {};
              const ev = editItems[item.id] || {};
              return (
                <tr key={item.id} style={{ backgroundColor: idx % 2 === 0 ? C.white : C.bg }}>
                  <td style={{ padding: "10px", color: C.slate, verticalAlign: "top" }}>{idx + 1}</td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    <div style={{ fontWeight: "600", color: C.navy }}>{d.asset_type_name}</div>
                    <div style={{ color: C.slate, fontSize: "12px" }}>{d.floor_level} · {d.location}</div>
                  </td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    <div style={{ color: C.slate, fontSize: "12px", marginBottom: [d.photo_1_url, d.photo_2_url, d.photo_3_url].filter(Boolean).length ? "8px" : 0 }}>{d.notes}</div>
                    {[d.photo_1_url, d.photo_2_url, d.photo_3_url].filter(Boolean).length > 0 && (
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        {[d.photo_1_url, d.photo_2_url, d.photo_3_url].filter(Boolean).map((url, pi) => (
                          <a key={pi} href={url} target="_blank" rel="noopener noreferrer">
                            <img src={url} alt={`Fail photo ${pi + 1}`} style={{ width: "64px", height: "48px", objectFit: "cover", borderRadius: "4px", border: `1px solid ${C.border}` }} />
                          </a>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    {editMode ? (
                      <textarea
                        value={ev.proposed_solution || ""}
                        onChange={(e) => setEditItems((p) => ({ ...p, [item.id]: { ...p[item.id], proposed_solution: e.target.value } }))}
                        rows={3}
                        style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "12px", resize: "vertical" }}
                      />
                    ) : (
                      <span style={{ color: C.dark }}>{item.proposed_solution}</span>
                    )}
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", verticalAlign: "top" }}>
                    {editMode ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "flex-end" }}>
                        <span style={{ color: C.slate }}>$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={ev.price_ex_gst || ""}
                          onChange={(e) => setEditItems((p) => ({ ...p, [item.id]: { ...p[item.id], price_ex_gst: e.target.value } }))}
                          style={{ width: "90px", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "13px", textAlign: "right" }}
                        />
                      </div>
                    ) : (
                      <>
                        <span style={{ fontWeight: "600", color: C.navy }}>${fmt2(item.price_ex_gst)}</span>
                        <div style={{ color: C.slate, fontWeight: "400", fontSize: "11px" }}>+GST</div>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      {(() => {
        const totalEx = editMode
          ? Object.values(editItems).reduce((a, v) => a + (parseFloat(v.price_ex_gst) || 0), 0)
          : Number(quote.total_ex_gst);
        const gst = totalEx * 0.1;
        const totalInc = totalEx * 1.1;
        return (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div style={{ border: `1px solid ${C.border}`, borderRadius: "8px", overflow: "hidden", minWidth: "260px" }}>
              {[
                { label: "Subtotal (ex. GST)", value: `$${fmt2(totalEx)}` },
                { label: "GST (10%)", value: `$${fmt2(gst)}` },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 14px", borderBottom: `1px solid ${C.border}`, fontSize: "13px" }}>
                  <span style={{ color: C.slate }}>{label}</span>
                  <span style={{ color: C.navy, fontWeight: "600" }}>{value}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", backgroundColor: C.navy }}>
                <span style={{ color: C.white, fontWeight: "700", fontSize: "14px" }}>TOTAL (inc. GST)</span>
                <span style={{ color: C.white, fontWeight: "700", fontSize: "14px" }}>${fmt2(totalInc)}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Edit mode save/cancel */}
      {editMode && (
        <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
          <button style={btn(C.slate, true)} onClick={() => setEditMode(false)} disabled={saving}>Cancel</button>
          <button style={btn(C.navy)} onClick={handleSaveEdit} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Request Detail ────────────────────────────────────────────────────────────

function RequestDetail({ requestId, onBack }) {
  const [request, setRequest] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/rectification-requests/${requestId}/`).then((r) => setRequest(r.data));
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  if (!request) return <div style={{ padding: "40px", color: C.slate }}>Loading…</div>;

  const quote = request.quote_detail || {};

  const handlePDF = async () => {
    setGeneratingPDF(true);
    try {
      const sigsRes = await axios.get(`${API}/tech-signatures/`);
      const signaturesMap = {};
      const toDataURL = (url) =>
        new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => {
            const c = document.createElement("canvas");
            c.width = img.width; c.height = img.height;
            c.getContext("2d").drawImage(img, 0, 0);
            resolve(c.toDataURL("image/png").split(",")[1]);
          };
          img.onerror = () => resolve(null);
          img.src = url;
        });
      for (const s of sigsRes.data) {
        if (s.signature_url) signaturesMap[s.tech_name] = await toDataURL(s.signature_url);
      }

      await generateRectificationRequestPDF(request, signaturesMap);
    } finally {
      setGeneratingPDF(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
        <BackButton onClick={onBack} />
        <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: C.navy, flex: 1 }}>
          RR-{String(request.id).padStart(3, "0")} — {quote.inspection_site_name}
        </h2>
        <StatusBadge status={request.status} />
        <button style={btn(C.teal)} onClick={handlePDF} disabled={generatingPDF}>
          <LuFileDown size={15} /> {generatingPDF ? "Generating…" : "Download PDF"}
        </button>
      </div>

      <div style={{ display: "flex", gap: "16px", marginBottom: "20px", flexWrap: "wrap" }}>
        <div style={{ fontSize: "13px", color: C.slate }}>
          <span style={{ fontWeight: "600", color: C.navy }}>Assigned to:</span> {request.assigned_to_name}
        </div>
        <div style={{ fontSize: "13px", color: C.slate }}>
          <span style={{ fontWeight: "600", color: C.navy }}>Created:</span> {new Date(request.created_at).toLocaleDateString("en-AU")}
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ backgroundColor: C.navy, color: C.white }}>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>#</th>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>Defect</th>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>Actions Taken</th>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>Photo</th>
              <th style={{ padding: "8px 10px", textAlign: "center" }}>Attested</th>
            </tr>
          </thead>
          <tbody>
            {(request.items || []).map((item, idx) => {
              const qi = item.quote_item_detail || {};
              const d = qi.inspection_item_detail || {};
              return (
                <tr key={item.id} style={{ backgroundColor: idx % 2 === 0 ? C.white : C.bg }}>
                  <td style={{ padding: "10px", color: C.slate, verticalAlign: "top" }}>{idx + 1}</td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    <div style={{ fontWeight: "600", color: C.navy }}>{d.asset_type_name}</div>
                    <div style={{ color: C.slate, fontSize: "12px" }}>{d.notes}</div>
                  </td>
                  <td style={{ padding: "10px", color: C.dark, fontSize: "13px", verticalAlign: "top" }}>
                    {item.actions_taken || <span style={{ color: C.slate, fontStyle: "italic" }}>Not yet filled</span>}
                  </td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    {item.photo_url ? (
                      <img src={item.photo_url} alt="completion" style={{ width: "80px", height: "60px", objectFit: "cover", borderRadius: "4px" }} />
                    ) : (
                      <span style={{ color: C.slate, fontSize: "12px", fontStyle: "italic" }}>None</span>
                    )}
                  </td>
                  <td style={{ padding: "10px", textAlign: "center", verticalAlign: "top" }}>
                    {item.attested
                      ? <span style={{ color: C.green, fontWeight: "700", fontSize: "14px" }}>✓</span>
                      : <span style={{ color: C.slate, fontSize: "12px" }}>Pending</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function RectificationsPage() {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("quotes");
  const [view, setView] = useState("list"); // list | new-quote | quote-detail | request-detail
  const [quotes, setQuotes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/rectification-quotes/`),
      axios.get(`${API}/rectification-requests/`),
      axios.get(`${API}/inspections/`),
    ]).then(([qr, rr, ir]) => {
      setQuotes(qr.data || []);
      setRequests(rr.data || []);
      setInspections(ir.data || []);
    }).catch((err) => {
      console.error("Rectifications load error:", err.response?.data || err.message);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDownloadQuotePDF = async (q) => {
    setDownloadingId(q.id);
    try {
      const res = await axios.get(`${API}/rectification-quotes/${q.id}/`);
      await generateRectificationQuotePDF(res.data, {});
    } catch (e) {
      alert("PDF generation failed");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteQuote = async (q) => {
    const msg = q.has_request
      ? "Delete this quote? The linked rectification request will also be deleted. This cannot be undone."
      : "Delete this quote? This cannot be undone.";
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/rectification-quotes/${q.id}/`);
      const prefix = `RQ-${String(q.id).padStart(3, "0")}_`;
      axios.get(`${API}/site-folders/?site=${q.inspection_site_id}`)
        .then((res) => {
          const docs = (res.data || []).flatMap((f) =>
            (f.documents || []).filter((d) => d.name.startsWith(prefix) && d.name.endsWith("_Quote.pdf"))
          );
          docs.forEach((d) => axios.delete(`${API}/site-documents/${d.id}/`).catch(console.warn));
        })
        .catch(console.warn);
      loadData();
    } catch (e) {
      alert("Delete failed: " + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    }
  };

  if (view === "new-quote") {
    return (
      <div style={{ padding: isMobile ? "16px" : "32px" }}>
        <QuoteForm
          inspections={inspections}
          quotes={quotes}
          onSaved={() => { loadData(); setView("list"); setTab("quotes"); }}
          onCancel={() => setView("list")}
        />
      </div>
    );
  }

  if (view === "quote-detail" && selectedId) {
    return (
      <div style={{ padding: isMobile ? "16px" : "24px", maxWidth: "1100px", margin: "0 auto" }}>
        <QuoteDetail
          quoteId={selectedId}
          onBack={() => { loadData(); setView("list"); }}
          onRequestCreated={(id) => { loadData(); setSelectedId(id); setView("request-detail"); setTab("open"); }}
        />
      </div>
    );
  }

  if (view === "request-detail" && selectedId) {
    return (
      <div style={{ padding: isMobile ? "16px" : "24px", maxWidth: "1100px", margin: "0 auto" }}>
        <RequestDetail
          requestId={selectedId}
          onBack={() => setView("list")}
        />
      </div>
    );
  }

  // List view
  return (
    <div>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Rectifications</div>
        {tab === "quotes" && (
          <button style={styles.addButton} onClick={() => setView("new-quote")}>
            <LuPlus size={15} /> New Quote
          </button>
        )}
      </div>
      <div style={styles.content}>
        <div style={styles.card}>
          {/* Tab nav */}
          <div style={{ display: "flex", borderBottom: `1px solid ${colors.border}`, padding: "0 20px" }}>
            {[["quotes", "Quotes"], ["open", "Open Defects"], ["completed", "Completed"]].map(([id, label]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                style={{
                  padding: "12px 16px",
                  fontSize: "13px",
                  fontWeight: "600",
                  border: "none",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  color: tab === id ? colors.text : "#64748b",
                  borderBottom: tab === id ? `2px solid ${colors.red}` : "2px solid transparent",
                  marginBottom: "-1px",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={styles.loading}>Loading…</div>
          ) : (() => {
            // Build flat defect lists from requests and quotes
            const openFromRequests = requests
              .filter((r) => r.status !== "completed")
              .flatMap((r) =>
                (r.items || []).map((item) => ({
                  type: "request",
                  requestId: r.id,
                  site: r.quote_detail?.inspection_site_name || "—",
                  asset: item.quote_item_detail?.inspection_item_detail?.asset_type_name || "—",
                  location: [
                    item.quote_item_detail?.inspection_item_detail?.floor_level,
                    item.quote_item_detail?.inspection_item_detail?.location,
                  ].filter(Boolean).join(" · "),
                  defect: item.quote_item_detail?.inspection_item_detail?.notes || "",
                  tech: r.assigned_to_name,
                  status: r.status,
                }))
              );

            const unassignedDefects = quotes
              .filter((q) => q.status === "approved" && !q.has_request)
              .flatMap((q) =>
                (q.items || []).map((item) => ({
                  type: "quote",
                  quoteId: q.id,
                  site: q.inspection_site_name || "—",
                  asset: item.inspection_item_detail?.asset_type_name || "—",
                  location: [
                    item.inspection_item_detail?.floor_level,
                    item.inspection_item_detail?.location,
                  ].filter(Boolean).join(" · "),
                  defect: item.inspection_item_detail?.notes || "",
                  tech: null,
                  status: "unassigned",
                }))
              );

            const openDefects = [...unassignedDefects, ...openFromRequests];

            const completedDefects = requests
              .filter((r) => r.status === "completed")
              .flatMap((r) =>
                (r.items || []).map((item) => ({
                  type: "request",
                  requestId: r.id,
                  site: r.quote_detail?.inspection_site_name || "—",
                  asset: item.quote_item_detail?.inspection_item_detail?.asset_type_name || "—",
                  location: [
                    item.quote_item_detail?.inspection_item_detail?.floor_level,
                    item.quote_item_detail?.inspection_item_detail?.location,
                  ].filter(Boolean).join(" · "),
                  defect: item.quote_item_detail?.inspection_item_detail?.notes || "",
                  tech: r.assigned_to_name,
                  status: "completed",
                }))
              );

            const defectList = tab === "open" ? openDefects : tab === "completed" ? completedDefects : [];

            if (tab === "quotes") {
              return quotes.length === 0 ? (
                <div style={styles.loading}>No rectification quotes yet. Click "New Quote" to create one.</div>
              ) : (
                <>
                  <div style={{ ...styles.tableHeader, gridTemplateColumns: "80px 2fr 1fr 1fr 1fr 100px" }}>
                    <div>Ref</div>
                    <div>Site</div>
                    <div>Type / Date</div>
                    <div>Status</div>
                    <div>Total inc. GST</div>
                    <div />
                  </div>
                  {quotes.map((q) => (
                    <div
                      key={q.id}
                      style={{ ...styles.tableRow(hovered === q.id), gridTemplateColumns: "80px 2fr 1fr 1fr 1fr 100px" }}
                      onMouseEnter={() => setHovered(q.id)}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => { setSelectedId(q.id); setView("quote-detail"); }}
                    >
                      <div style={{ fontWeight: "600", color: colors.text }}>RQ-{String(q.id).padStart(3, "0")}</div>
                      <div style={{ fontWeight: "500" }}>{q.inspection_site_name}</div>
                      <div style={{ color: "#64748b" }}>
                        {(q.inspection_type || "").replace("_", "-")} · {q.inspection_date ? new Date(q.inspection_date).toLocaleDateString("en-AU") : ""}
                      </div>
                      <div><StatusBadge status={q.status} /></div>
                      <div style={{ fontWeight: "600" }}>
                        ${Number(q.total_inc_gst).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div style={{ display: "flex", gap: "2px", alignItems: "center", justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                        <button
                          title="Download PDF"
                          onClick={() => handleDownloadQuotePDF(q)}
                          disabled={downloadingId === q.id}
                          style={{ background: "none", border: "none", cursor: "pointer", color: colors.blue, padding: "4px", borderRadius: "4px", display: "flex", alignItems: "center" }}
                        >
                          <LuFileDown size={14} />
                        </button>
                        <button
                          title="Edit"
                          onClick={() => { setSelectedId(q.id); setView("quote-detail"); }}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: "4px", borderRadius: "4px", display: "flex", alignItems: "center" }}
                        >
                          <LuPencil size={14} />
                        </button>
                        <button
                          title="Delete"
                          onClick={() => handleDeleteQuote(q)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: colors.red, padding: "4px", borderRadius: "4px", display: "flex", alignItems: "center" }}
                        >
                          <LuTrash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                  <div style={styles.count}>{quotes.length} quote{quotes.length !== 1 ? "s" : ""}</div>
                </>
              );
            }

            // Open / Completed defect table
            return defectList.length === 0 ? (
              <div style={styles.loading}>
                {tab === "open" ? "No open defects." : "No completed defects yet."}
              </div>
            ) : (
              <>
                <div style={{ ...styles.tableHeader, gridTemplateColumns: "2fr 3fr 1fr 1fr" }}>
                  <div>Site</div>
                  <div>Asset / Defect</div>
                  <div>Assigned To</div>
                  <div>Status</div>
                </div>
                {defectList.map((d, idx) => (
                  <div
                    key={idx}
                    style={{ ...styles.tableRow(hovered === idx), gridTemplateColumns: "2fr 3fr 1fr 1fr" }}
                    onMouseEnter={() => setHovered(idx)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => {
                      if (d.type === "request") { setSelectedId(d.requestId); setView("request-detail"); }
                      else { setSelectedId(d.quoteId); setView("quote-detail"); }
                    }}
                  >
                    <div style={{ fontWeight: "500", color: colors.text }}>{d.site}</div>
                    <div>
                      <div style={{ fontWeight: "500" }}>
                        {d.asset}{d.location ? <span style={{ color: "#64748b", fontWeight: "400" }}> · {d.location}</span> : null}
                      </div>
                      {d.defect && <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>{d.defect}</div>}
                    </div>
                    <div style={{ color: "#64748b" }}>{d.tech || <span style={{ fontStyle: "italic" }}>Unassigned</span>}</div>
                    <div><StatusBadge status={d.status} /></div>
                  </div>
                ))}
                <div style={styles.count}>{defectList.length} defect{defectList.length !== 1 ? "s" : ""}</div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
